/**
 * The Agenda opens at NOW (2026-10-30, after the first review): on a
 * 1280×720 laptop a morning's failures filled the box and the now line
 * sat below the fold.
 *
 * Scrolls the Agenda's OWN scroller -- never the page, never focus -- so
 * the now line sits near the top with about two rows above it (what just
 * happened stays in sight, and everything earlier is a scroll up away):
 *
 *  - when the Agenda first draws a range that includes today (switching
 *    into it is a first draw: it mounts), at once;
 *  - when the range moves onto today (Today, or ‹ › into it), smoothly
 *    unless the reader asked for reduced motion; a range that moves off
 *    today starts at its top instead.
 *
 * It waits for the now line (the day's rows may still be loading) and
 * then does it ONCE: a data refresh or the minute tick never scrolls
 * again, and once the reader has moved the list (wheel, touch, keys, the
 * scrollbar) a scroll still pending is dropped. While it scrolls, the box
 * is `data-tuck-quiet`: the shell records where it went but does not tuck
 * the top bar for a move the reader did not make (`use-scroll-tuck.ts`).
 */
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

/** Rows of context kept above the now line. */
export const ROWS_ABOVE_NOW = 2;

const NOW_LINE = '[data-testid="agenda-nowline"]';

/** Where the scroller should stand for the now line to sit under the
 *  sticky heads with `ROWS_ABOVE_NOW` rows above it. */
export function nowScrollTop(scroller: HTMLElement, line: Element): number {
  let anchor: Element = line;
  for (let i = 0; i < ROWS_ABOVE_NOW; i += 1) {
    const prev = anchor.previousElementSibling;
    if (!prev) break;
    anchor = prev;
  }
  const head = scroller.querySelector<HTMLElement>('.nx-agenda__head');
  const dayHead = line
    .closest('.nx-agenda__day')
    ?.querySelector<HTMLElement>('.nx-agenda__day-head');
  const sticky = (head?.offsetHeight ?? 0) + (dayHead?.offsetHeight ?? 0);
  const top =
    anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
  return Math.max(0, Math.round(top - sticky));
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

export function useOpenAtNow(
  scrollRef: RefObject<HTMLElement | null>,
  args: { dateKey: string; includesToday: boolean },
): void {
  const { dateKey, includesToday } = args;
  /** A scroll owed: how, or null when none is. */
  const pending = useRef<ScrollBehavior | null>(includesToday ? 'auto' : null);
  const lastDate = useRef(dateKey);
  const timers = useRef<number[]>([]);

  // The reader moving the list cancels anything still owed.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const mine = () => {
      pending.current = null;
    };
    const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
    for (const e of events) el.addEventListener(e, mine, { passive: true });
    return () => {
      for (const e of events) el.removeEventListener(e, mine);
    };
  }, [scrollRef]);

  useEffect(
    () => () => {
      for (const t of timers.current) window.clearTimeout(t);
    },
    [],
  );

  // Every render: a new range owes a scroll; one owed is paid as soon as
  // the now line is there.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (lastDate.current !== dateKey) {
      lastDate.current = dateKey;
      pending.current = includesToday ? (reducedMotion() ? 'auto' : 'smooth') : null;
      if (!includesToday && el) el.scrollTop = 0;
    }
    const how = pending.current;
    if (!how || !el) return;
    const line = el.querySelector(NOW_LINE);
    if (!line) return;
    pending.current = null;
    const top = nowScrollTop(el, line);
    el.setAttribute('data-tuck-quiet', '');
    const done = () => el.removeAttribute('data-tuck-quiet');
    if (typeof el.scrollTo === 'function') el.scrollTo({ top, behavior: how });
    else el.scrollTop = top;
    if (how === 'smooth') el.addEventListener('scrollend', done, { once: true });
    // The scroll event of a jump arrives a frame later; a smooth scroll
    // may not report its end (no `scrollend`): a timer covers both.
    timers.current.push(window.setTimeout(done, how === 'smooth' ? 1200 : 200));
  });
}
